// Phase 9.1.B — Client portal signup.
//
// Calls POST /portal/auth/register. On success, shows a "check your email"
// state (no immediate session — the verification email link is the bridge).
// 12-char password minimum enforced both client-side (UX) and server-side
// (services/portal-auth.ts via hashPassword).
import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError, api } from "@/lib/api";

interface RegisterResponse {
  user_id: string;
  linked_to_existing_contact: boolean;
  verification_email_sent: boolean;
}

export function SignupPage() {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<RegisterResponse | null>(null);

  if (user) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await api<RegisterResponse>("/portal/auth/register", {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          company_name: companyName,
        }),
        skipAuth: true,
      });
      setSuccess(result);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError("An account with that email already exists. Try signing in.");
        } else if (err.status === 400) {
          setError(
            "Couldn't create the account. Check that your password is at least 12 characters and email looks right.",
          );
        } else if (err.status === 429 || err.status === 503) {
          // Sign-up limits (security audit 2026-10-21): per address, or busy.
          const msg = (err.body as { message?: string } | null)?.message;
          setError(msg ?? "Too many sign-ups right now. Please try again in a little while.");
        } else {
          setError("Something went wrong. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-md bg-peach-400">
            <span className="text-2xl font-bold text-brown-500">✓</span>
          </div>
          <h1 className="mb-4 font-display text-3xl font-bold tracking-tight">
            Check your email
          </h1>
          <p className="mb-8 text-brown-400">
            We sent a verification link to{" "}
            <span className="font-semibold text-brown-500">{email}</span>. Click
            the link to activate your account — it expires in 24 hours.
          </p>
          <p className="text-sm text-brown-400">
            Didn't get it?{" "}
            <Link
              to="/login"
              className="font-semibold text-ink underline-offset-2 hover:underline"
            >
              Go to sign-in
            </Link>{" "}
            and use "Resend verification email".
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">
          Create your account
        </h1>
        <p className="mb-10 text-brown-400">
          Set up access to your other portal in under a minute.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-brown-500">
                First name
              </span>
              <input
                type="text"
                required
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
                autoComplete="given-name"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-brown-500">
                Last name
              </span>
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
                autoComplete="family-name"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Company name
            </span>
            <input
              type="text"
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="organization"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Email
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="email"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Password
            </span>
            <input
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="new-password"
            />
            <span className="mt-1 block text-xs text-brown-300">
              12+ characters.
            </span>
          </label>

          {error && (
            <div className="rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ink px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="mt-8 text-sm text-brown-400">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-ink underline-offset-2 hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
